import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Zap, Shield, Users, Headphones, Clock, Flame, Loader2, Building, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import PricingCard from '../components/PricingCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { paymentAPI } from '../utils/api';
import authService from '../firebase/authService';
import { GridBackground } from '../components/TechUI';
import { fetchSubscriptionPlans, getTierPricing, getDiscountTimeRemaining, formatPrice } from '../utils/pricing';
import { openCashfreeCheckout } from '../utils/cashfree';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [user, setUser] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({ hours: 23, minutes: 59, seconds: 59, expired: false });
  const [plans, setPlans] = useState([]);
  const [currency, setCurrency] = useState('INR');

  // Fetch plans from API
  useEffect(() => {
    const loadPlans = async () => {
      setLoadingPlans(true);
      try {
        const data = await fetchSubscriptionPlans();

        // Filter out free and enterprise plans, format for display
        const paidPlans = (data.plans || [])
          .filter(plan => plan.tier !== 'free' && plan.tier !== 'enterprise')
          .map(plan => ({
            tier: plan.tier,
            name: plan.name,
            description: plan.description || getDurationLabel(plan.duration_months),
            price: plan.price || 0,
            originalPrice: plan.original_price || plan.price || 0,
            durationMonths: plan.duration_months || 1,
            discountPercent: plan.discount_percent || 0,
            features: plan.features || [],
            isPopular: plan.tier === 'ultimate',  // Make Ultimate the highlighted one
            currency: plan.currency || 'INR'
          }));

        // Reorder: Starter, Ultimate (middle/featured), Professional
        const orderedPlans = [
          paidPlans.find(p => p.tier === 'starter'),
          paidPlans.find(p => p.tier === 'ultimate'),
          paidPlans.find(p => p.tier === 'professional')
        ].filter(Boolean);

        setPlans(orderedPlans);
        setCurrency(data.currency || 'INR');
      } catch (error) {
        console.error('Error loading plans:', error);
        toast.error('Failed to load pricing. Please refresh.');
      } finally {
        setLoadingPlans(false);
      }
    };

    loadPlans();
  }, []);

  const getDurationLabel = (months) => {
    if (months === 1) return '1 Month Access';
    if (months === 3) return '3 Months Access';
    if (months === 12) return '12 Months Access';
    return `${months} Months Access`;
  };

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);

    if (!currentUser) {
      toast('Please sign in to subscribe', { icon: '🔐' });
    }
  }, []);

  // Countdown timer for discount expiry
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(getDiscountTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const launchCashfreeCheckout = async (paymentData) => {
    const checkoutMode = paymentData?.environment === 'production' ? 'production' : 'sandbox';

    if (paymentData?.payment_session_id) {
      try {
        await openCashfreeCheckout({
          paymentSessionId: paymentData.payment_session_id,
          mode: checkoutMode,
          redirectTarget: '_self'
        });
        return;
      } catch (error) {
        console.error('Cashfree checkout error:', error);
        if (paymentData?.payment_link) {
          window.location.href = paymentData.payment_link;
          return;
        }
        throw new Error('Failed to open Cashfree checkout. Please try again.');
      }
    }

    if (paymentData?.payment_link) {
      window.location.href = paymentData.payment_link;
      return;
    }

    throw new Error('Invalid payment response. Please try again.');
  };

  const handleSelectPlan = async (tier) => {
    if (tier === 'free') {
      toast('You are already on the free plan', { icon: 'ℹ️' });
      return;
    }

    setLoading(true);

    try {
      // Get current user directly from authService for immediate check
      const currentUser = authService.getCurrentUser();

      if (!currentUser) {
        toast.error('Please sign in to subscribe');
        setLoading(false);
        return;
      }

      // Require email verification before purchase
      if (!authService.isEmailVerified()) {
        toast.error('Please verify your email address before purchasing a plan. Check your inbox for the verification link.', { duration: 5000 });
        // Offer to resend
        try {
          await authService.resendVerificationEmail();
          toast('Verification email resent! Check your inbox.', { icon: '📩', duration: 4000 });
        } catch (e) {
          // silent
        }
        setLoading(false);
        return;
      }

      // Get user details
      const userName = currentUser.displayName || 'User';
      const userEmail = currentUser.email || '';
      const userPhone = currentUser.phoneNumber || '';

      // Create subscription payment (fixed duration - no billing cycle)
      const response = await paymentAPI.createSubscriptionPayment(
        tier,
        'one_time',  // Fixed duration plans are one-time purchases
        userName,
        userEmail,
        userPhone
      );

      if (!response || !response.order_id) {
        throw new Error('Invalid response from payment gateway');
      }

      // Store order details for later verification
      localStorage.setItem('pending_order', JSON.stringify({
        order_id: response.order_id,
        tier: tier,
        billing_cycle: 'one_time',
        amount: response.amount
      }));

      await launchCashfreeCheckout(response);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  if (loadingPlans) {
    return (
      <GridBackground className="min-h-screen">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-white font-mono">Loading pricing...</p>
          </div>
        </div>
      </GridBackground>
    );
  }

  return (
    <GridBackground className="min-h-screen">
      <div className="relative z-20 max-w-7xl mx-auto px-4 py-12">
        {/* Urgency Banner */}
        {!timeRemaining.expired && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-2xl p-4 flex items-center justify-center space-x-4">
              <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
              <div className="text-center">
                <p className="text-white font-bold font-mono">
                  🔥 LIMITED TIME OFFER - UP TO 50% OFF!
                </p>
                <div className="flex items-center justify-center space-x-2 mt-1">
                  <Clock className="w-4 h-4 text-orange-400" />
                  <span className="text-orange-400 font-mono text-sm">
                    Expires in: {String(timeRemaining.hours).padStart(2, '0')}h {String(timeRemaining.minutes).padStart(2, '0')}m {String(timeRemaining.seconds).padStart(2, '0')}s
                  </span>
                </div>
              </div>
              <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
            </div>
          </motion.div>
        )}

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-6 py-3 mb-6">
            <Shield className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-mono uppercase text-sm font-bold">Secure Payments via Cashfree</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-mono">
            CHOOSE YOUR <span className="text-emerald-400">PLAN</span>
          </h1>

          <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8 font-mono">
            Invest in your career with AI-powered guidance, personalized roadmaps, and expert support
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.tier}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <PricingCard
                {...plan}
                //billingCycle={billingCycle}
                currency={currency}
                onSelect={() => handleSelectPlan(plan.tier)}
              />
            </motion.div>
          ))}
        </div>

        {/* Enterprise / B2B Tier CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-slate-900 to-slate-800 border-2 border-slate-700 hover:border-cyan-500/50 rounded-3xl p-8 mb-16 flex flex-col md:flex-row items-center justify-between shadow-2xl transition-all"
        >
          <div className="text-left mb-6 md:mb-0 md:mr-8">
            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-1.5 mb-4">
              <Building className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 font-mono text-xs font-bold tracking-wider">ENTERPRISE & EDUCATION</span>
            </div>
            <h3 className="text-3xl font-bold text-white mb-2 font-mono">For Bootcamps, Universities, and Corporate Teams</h3>
            <p className="text-slate-400 max-w-2xl font-mono text-sm">
              Get bulk access for your students or employees. Organization admins can create invite codes, track student onboarding readiness, and monitor skill progression across entire cohorts.
            </p>
          </div>

          <div className="shrink-0 flex flex-col items-center">
            <button
              onClick={() => navigate('/b2b-admin')}
              className="px-8 py-4 bg-white hover:bg-slate-200 text-slate-900 font-bold rounded-xl whitespace-nowrap shadow-lg transition-transform hover:scale-105 flex items-center gap-2"
            >
              Go to Admin Portal <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => window.location.href = "mailto:sales@rastorai.com"}
              className="mt-3 text-sm text-cyan-400 hover:text-cyan-300 underline font-mono"
            >
              Contact Sales for Pricing
            </button>
          </div>
        </motion.div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#0F131F] border border-white/10 rounded-3xl p-8 mb-16"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center font-mono">FEATURE COMPARISON</h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-slate-400 font-mono text-sm">FEATURE</th>
                  {plans.map(plan => (
                    <th
                      key={plan.tier}
                      className={`text-center py-4 px-4 font-mono ${plan.isPopular ? 'text-emerald-400' : 'text-white'}`}
                    >
                      {plan.name.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Generate rows from plan features */}
                {(() => {
                  // Collect all unique features across all plans
                  const allFeatures = new Set();
                  plans.forEach(plan => {
                    (plan.features || []).forEach(feature => allFeatures.add(feature));
                  });

                  return Array.from(allFeatures).map((feature, index) => (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="py-4 px-4 text-slate-300 font-mono text-sm">{feature}</td>
                      {plans.map(plan => {
                        const hasFeature = (plan.features || []).includes(feature);
                        return (
                          <td key={plan.tier} className="py-4 px-4 text-center">
                            {hasFeature ? (
                              <Check className={`w-5 h-5 mx-auto ${plan.isPopular ? 'text-emerald-400' : 'text-emerald-400'}`} />
                            ) : (
                              <X className="w-5 h-5 text-slate-600 mx-auto" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Trust Signals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="bg-[#0F131F] border border-white/10 rounded-2xl p-6 text-center">
            <Users className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2 font-mono">10,000+</h3>
            <p className="text-slate-400 text-sm font-mono">Successful Career Transitions</p>
          </div>
          <div className="bg-[#0F131F] border border-white/10 rounded-2xl p-6 text-center">
            <Shield className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2 font-mono">100%</h3>
            <p className="text-slate-400 text-sm font-mono">Secure & Encrypted</p>
          </div>
          <div className="bg-[#0F131F] border border-white/10 rounded-2xl p-6 text-center">
            <Headphones className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2 font-mono">24/7</h3>
            <p className="text-slate-400 text-sm font-mono">Expert Support</p>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center font-mono">WHAT OUR USERS SAY</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Priya M.', role: 'Software Engineer', text: 'Rastor AI helped me land a senior role in 4 months. The roadmap was incredibly precise.' },
              { name: 'Rahul K.', role: 'Product Manager', text: 'The AI-generated learning paths saved me weeks of research. Worth every rupee!' },
              { name: 'Sneha D.', role: 'Data Analyst', text: 'I went from zero to interview-ready in 8 weeks following my personalized roadmap.' },
            ].map((t, i) => (
              <div key={i} className="bg-[#0F131F] border border-white/10 rounded-2xl p-6">
                <p className="text-slate-300 font-mono text-sm mb-4 italic">"{t.text}"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold font-mono">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="text-white text-sm font-bold font-mono">{t.name}</div>
                    <div className="text-slate-500 text-xs font-mono">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-[#0F131F] border border-white/10 rounded-3xl p-8"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center font-mono">FREQUENTLY ASKED QUESTIONS</h2>

          <div className="space-y-6 max-w-3xl mx-auto">
            {[
              {
                q: 'Can I switch plans later?',
                a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect in the next billing cycle.'
              },
              {
                q: 'Is there a free trial?',
                a: 'We offer a generous free tier so you can explore the platform before purchasing. All paid plans are non-refundable as services are delivered instantly.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit/debit cards, UPI, net banking, and digital wallets through our secure payment partner.'
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Absolutely! Cancel anytime from your account settings. You\'ll retain access until the end of your billing period.'
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-white/10 pb-6">
                <h3 className="text-lg font-bold text-white mb-2 font-mono">{faq.q}</h3>
                <p className="text-slate-400 font-mono text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {loading && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="text-center">
              <LoadingSpinner />
              <p className="text-white mt-4 font-mono">Processing payment...</p>
            </div>
          </div>
        )}
      </div>
    </GridBackground>
  );
};

export default SubscriptionPage;
